import React, { useRef, useState, useMemo } from "react";
import { api } from "../../lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Reel upload panel for Creative Studio.
 *
 *  - pick an mp4 file;
 *  - write a caption (required);
 *  - optionally paste the voiceover transcript;
 *  - optionally paste the on-screen text (one card per line);
 *  - POST multipart to /api/brand-os/creative/asset-groups/:id/reel/upload.
 *  - server-side validates the mp4 (duration / size / 9:16 aspect / codec)
 *    AND runs each of {caption, transcript, on-screen text} through the
 *    FULL governed gate before storing.
 *  - BLOCK on any surface → nothing is stored, surface-tagged findings
 *    are rendered side by side; the operator edits + retries.
 *  - PASS or NEEDS_REVIEW across every surface → the reel is stored
 *    and the per-surface verdicts render inline for the review queue.
 */
export default function ReelUploadPanel({ assetGroupId }) {
  const fileRef = useRef(null);
  // P4 — optional 30s + 15s cuts uploaded in one call. Full cut is
  // required (``file``); the two secondary cuts are picked up by the
  // backend only if they're attached.
  const cut30Ref = useRef(null);
  const cut15Ref = useRef(null);
  const [caption, setCaption] = useState("");
  const [transcript, setTranscript] = useState("");
  const [onScreenText, setOnScreenText] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);

  async function onUpload() {
    setErr(null);
    setResult(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setErr({ message: "Pick an mp4 first." });
      return;
    }
    if (!caption.trim()) {
      setErr({ message: "A caption is required (it runs through the gate)." });
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("caption", caption);
    if (transcript.trim()) fd.append("transcript", transcript.trim());
    if (onScreenText.trim()) fd.append("on_screen_text", onScreenText.trim());
    // P4 — attach optional 30s / 15s cuts if the operator picked them.
    const c30 = cut30Ref.current?.files?.[0];
    const c15 = cut15Ref.current?.files?.[0];
    if (c30) fd.append("cut_30s_file", c30);
    if (c15) fd.append("cut_15s_file", c15);
    setUploading(true);
    try {
      // The server-side gate pass can take 60-90s (never-say + grounding
      // + Guardian LLM review runs over 3 surfaces). The default axios
      // timeout is 15s, which showed a false "upload failed: timeout" to
      // Roger in prod even on successful uploads. Raise to 180s for this
      // one call; the UI shows the "gating…" copy below the button.
      const r = await api.post(
        `/creative/asset-groups/${assetGroupId}/reel/upload`,
        fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 180_000,
        },
      );
      setResult(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || { message: e.message });
    } finally {
      setUploading(false);
    }
  }

  // Human-readable labels for the surface tags the server emits.
  const SURFACE_LABEL = {
    reel_caption: "Caption",
    reel_transcript: "Transcript",
    reel_on_screen_text: "On-screen text",
  };

  function verdictBadgeVariant(v) {
    if (v === "block") return "destructive";
    if (v === "needs_review") return "secondary";
    return "default";
  }

  return (
    <Card
      data-testid="reel-upload-panel"
      style={{ padding: 16, display: "grid", gap: 12 }}
    >
      <div style={{ display: "flex", justifyContent: "space-between",
                     alignItems: "center" }}>
        <h3 style={{ margin: 0, fontSize: 16 }}>Reel upload</h3>
        <Badge variant="secondary">kind: reel</Badge>
      </div>
      <input
        type="file" accept="video/mp4"
        ref={fileRef}
        data-testid="reel-file-input"
      />
      {/* P4 — optional 30s + 15s cuts. Uploaded in the SAME call so
          they're gated and frozen as one set with the full cut.
          Backend duration caps: full ≤ 90s · 30s ≤ 32s · 15s ≤ 17s. */}
      <div
        data-testid="reel-cut-inputs"
        style={{
          display: "grid", gap: 8, padding: 10,
          border: "1px dashed var(--bo-line, #d9d4c0)",
          borderRadius: 8,
          background: "var(--bo-panel-alt, #faf8f2)",
        }}
      >
        <div style={{ fontSize: 11, color: "#777", lineHeight: 1.4 }}>
          Optional secondary cuts — uploaded together, gated as one set.
          Auto-picked per surface (IG/FB Story prefers 15s · YouTube Short
          now up to 180s · TikTok prefers full).
        </div>
        <div style={{ display: "grid", gap: 4 }}>
          <label style={{ fontSize: 12, color: "#555" }}>
            30-second cut (optional · ≤ 32s)
          </label>
          <input
            type="file" accept="video/mp4"
            ref={cut30Ref}
            data-testid="reel-cut-30s-input"
          />
        </div>
        <div style={{ display: "grid", gap: 4 }}>
          <label style={{ fontSize: 12, color: "#555" }}>
            15-second cut (optional · ≤ 17s)
          </label>
          <input
            type="file" accept="video/mp4"
            ref={cut15Ref}
            data-testid="reel-cut-15s-input"
          />
        </div>
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12, color: "#555" }}>Caption</label>
        <Textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="Reel caption (goes through the full governed gate: never-say, grounding, single link, single email)…"
          rows={4}
          data-testid="reel-caption-input"
        />
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12, color: "#555" }}>
          Voiceover transcript (optional)
        </label>
        <Textarea
          value={transcript}
          onChange={(e) => setTranscript(e.target.value)}
          placeholder="Paste the spoken narration verbatim. Guardian will review it against the Truth Book + Register, same as the caption."
          rows={4}
          data-testid="reel-transcript-input"
        />
      </div>
      <div style={{ display: "grid", gap: 4 }}>
        <label style={{ fontSize: 12, color: "#555" }}>
          On-screen text (optional)
        </label>
        <Textarea
          value={onScreenText}
          onChange={(e) => setOnScreenText(e.target.value)}
          placeholder="Paste every text card the viewer sees, one per line."
          rows={5}
          data-testid="reel-on-screen-text-input"
        />
        <div
          style={{ fontSize: 11, color: "#666" }}
          data-testid="reel-on-screen-text-hint"
        >
          <strong>Heads up:</strong> any URL on screen must be written in full
          with the scheme — e.g. <code>https://precipice-health.com</code>, not
          the bare domain. Bare domains trip the single-link rule.
        </div>
      </div>
      <div>
        <Button
          onClick={onUpload} disabled={uploading}
          data-testid="reel-upload-submit"
        >
          {uploading
            ? "Validating + gating…"
            : "Upload & gate every text surface"}
        </Button>
        {uploading && (
          <div
            style={{ marginTop: 8, fontSize: 11, color: "#666" }}
            data-testid="reel-upload-gating-hint"
          >
            Gating caption, transcript, and on-screen text through
            Guardian — this can take up to ~90&nbsp;s. Please don't
            close the tab.
          </div>
        )}
      </div>
      {err && (
        <Card style={{ padding: 12, color: "crimson" }}
              data-testid="reel-upload-error">
          <div style={{ fontWeight: 600 }}>
            {err.code || "Upload failed"}
          </div>
          <div>{err.message}</div>
          {Array.isArray(err.blocked_surfaces)
            && err.blocked_surfaces.length > 0 && (
            <div
              style={{ marginTop: 8 }}
              data-testid="reel-upload-error-surfaces"
            >
              <div style={{ fontSize: 12, color: "#444" }}>
                Blocked surfaces:
                {" "}
                {err.blocked_surfaces
                  .map((s) => SURFACE_LABEL[s] || s)
                  .join(", ")}
              </div>
              {err.findings_by_surface
                && Object.entries(err.findings_by_surface).map(
                  ([s, findings]) => (
                    <div
                      key={s}
                      style={{ marginTop: 8 }}
                      data-testid={`reel-upload-error-${s}`}
                    >
                      <div style={{ fontWeight: 600 }}>
                        {SURFACE_LABEL[s] || s}
                      </div>
                      <ul style={{ marginTop: 4 }}>
                        {(findings || []).map((f, i) => (
                          <li key={i}>
                            <strong>
                              {f.layer || f.rule || "block"}
                            </strong>
                            : {f.message || f.why || f.match_excerpt
                               || f.term || ""}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ),
                )}
            </div>
          )}
          {/* Backward-compat: single-surface (caption-only) errors still
              render as a flat findings list. */}
          {Array.isArray(err.findings) && err.findings.length > 0
            && !err.blocked_surfaces && (
            <ul style={{ marginTop: 8 }}>
              {err.findings.map((f, i) => (
                <li key={i}>
                  <strong>{f.rule || f.layer}</strong>: {f.message}
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}
      {result && (
        <ReelResultCard
          result={result}
          assetGroupId={assetGroupId}
          surfaceLabel={SURFACE_LABEL}
          verdictBadgeVariant={verdictBadgeVariant}
        />
      )}
      {result && !result.asset_set_frozen && result.reel_asset_id && (
        <AttachCutPanel
          reelAssetId={result.reel_asset_id}
          existingCuts={result.cuts || []}
          onAttached={(updatedCuts) => {
            setResult((prev) => prev ? { ...prev, cuts: updatedCuts } : prev);
          }}
        />
      )}
    </Card>
  );
}


/**
 * P4 — Attach a single cut (full / 30s / 15s) to an existing,
 * unfrozen reel group. Lets Roger finish an edit later and still
 * ship it as part of the same governed set. Replaces an existing
 * cut with the same ``cut_key``.
 */
function AttachCutPanel({ reelAssetId, existingCuts, onAttached }) {
  const fileRef = useRef(null);
  const [cutKey, setCutKey] = useState("30s");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);

  const uploadedKeys = useMemo(
    () => new Set((existingCuts || []).map((c) => c.cut_key)),
    [existingCuts],
  );

  async function onAttach() {
    setErr(null); setOk(null);
    const file = fileRef.current?.files?.[0];
    if (!file) { setErr({ message: "Pick an mp4 first." }); return; }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("cut_key", cutKey);
    setBusy(true);
    try {
      const r = await api.post(
        `/creative/reels/${reelAssetId}/cuts`, fd,
        { headers: { "Content-Type": "multipart/form-data" },
          timeout: 180_000 },
      );
      setOk(`Attached ${cutKey} cut (${r.data?.cuts?.length || 0} total).`);
      if (onAttached) onAttached(r.data?.cuts || []);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      setErr(e?.response?.data?.detail || { message: e.message });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card
      style={{ padding: 12, display: "grid", gap: 8 }}
      data-testid="reel-attach-cut-panel"
    >
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center" }}>
        <h4 style={{ margin: 0, fontSize: 14 }}>Attach another cut</h4>
        <Badge variant="secondary">unfrozen</Badge>
      </div>
      <div style={{ fontSize: 11, color: "#666" }}>
        Already uploaded: {uploadedKeys.size
          ? [...uploadedKeys].join(", ")
          : "none"}. Attaching an existing key REPLACES it.
      </div>
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <label style={{ fontSize: 12 }}>Cut:</label>
        <select
          data-testid="reel-attach-cut-key"
          value={cutKey}
          onChange={(e) => setCutKey(e.target.value)}
          style={{ padding: "4px 8px" }}
        >
          <option value="full">full · ≤ 90s</option>
          <option value="30s">30s · ≤ 32s</option>
          <option value="15s">15s · ≤ 17s</option>
        </select>
        <input
          type="file" accept="video/mp4" ref={fileRef}
          data-testid="reel-attach-cut-file"
          style={{ flex: 1 }}
        />
      </div>
      <Button
        onClick={onAttach} disabled={busy}
        data-testid="reel-attach-cut-submit"
      >
        {busy ? "Attaching…" : "Attach cut"}
      </Button>
      {err && (
        <div
          data-testid="reel-attach-cut-error"
          style={{ color: "crimson", fontSize: 12 }}
        >
          <strong>{err.code || "Error"}:</strong> {err.message}
        </div>
      )}
      {ok && (
        <div
          data-testid="reel-attach-cut-ok"
          style={{ color: "var(--bo-success, green)", fontSize: 12 }}
        >
          {ok}
        </div>
      )}
    </Card>
  );
}


/**
 * Result card for a just-uploaded reel. Shows per-surface verdicts,
 * per-surface "Sign off" buttons for needs_review surfaces, a
 * "Freeze for publishing" button that stays disabled until every
 * surface is cleared (tooltip lists the blockers), and the public
 * MP4 URL with a copy button.
 */
function ReelResultCard({
  result, assetGroupId, surfaceLabel, verdictBadgeVariant,
}) {
  const reelAssetId = result?.reel_asset_id;
  // Local view of per-surface state so signoffs apply without a reload.
  const [verdicts, setVerdicts] = useState(
    () => ({ ...(result.gate_verdicts || {
      reel_caption: result.gate_verdict || "pass",
    }) }),
  );
  // surface → owner signoff present on THIS reel.
  const [signedOff, setSignedOff] = useState({});
  // surface → optional note being typed.
  const [notes, setNotes] = useState({});
  // surface → in-flight state.
  const [busySurface, setBusySurface] = useState(null);
  const [signoffErr, setSignoffErr] = useState(null);

  // Freeze state.
  const [frozen, setFrozen] = useState(null);
  const [freezeBusy, setFreezeBusy] = useState(false);
  const [freezeErr, setFreezeErr] = useState(null);

  // Copy-URL state.
  const [copied, setCopied] = useState(false);

  // Which surfaces still block freeze?
  const blockers = useMemo(() => {
    const out = [];
    for (const [s, v] of Object.entries(verdicts)) {
      if (v === "pass") continue;
      if (v === "block") out.push({ surface: s, kind: "block" });
      else if (v === "needs_review" && !signedOff[s])
        out.push({ surface: s, kind: "needs_review" });
    }
    return out;
  }, [verdicts, signedOff]);

  const canFreeze = blockers.length === 0 && !frozen;

  async function onSignoff(surface) {
    setSignoffErr(null);
    setBusySurface(surface);
    try {
      const r = await api.post(
        `/creative/reels/${reelAssetId}/signoff`,
        { surfaces: [surface], note: notes[surface] || "" },
      );
      // Server echoes the signed_off rows; mark surface cleared.
      if (r.data?.count > 0 || r.data?.already_signed_off) {
        setSignedOff((prev) => ({ ...prev, [surface]: true }));
      }
    } catch (e) {
      setSignoffErr({
        surface,
        detail: e?.response?.data?.detail || { message: e.message },
      });
    } finally {
      setBusySurface(null);
    }
  }

  async function onFreeze() {
    setFreezeErr(null);
    setFreezeBusy(true);
    try {
      const r = await api.post(
        `/creative/reels/${reelAssetId}/freeze`,
        {},
      );
      setFrozen(r.data?.approved_asset_set || null);
    } catch (e) {
      setFreezeErr(e?.response?.data?.detail || { message: e.message });
    } finally {
      setFreezeBusy(false);
    }
  }

  async function onCopyUrl() {
    try {
      await navigator.clipboard.writeText(result.video_url || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) {
      // Clipboard blocked (iframes, http); fall back to prompt.
      window.prompt("Copy the public MP4 URL:", result.video_url || "");
    }
  }

  const blockerTooltip = blockers.length === 0
    ? "Freeze into a publish-ready asset set."
    : "Blocked by: " + blockers.map(
      (b) => `${surfaceLabel[b.surface] || b.surface} (${
        b.kind === "block" ? "re-upload needed" : "needs owner sign-off"
      })`,
    ).join(", ");

  return (
    <Card style={{ padding: 12 }} data-testid="reel-upload-ok">
      <div style={{ display: "flex", justifyContent: "space-between",
                     alignItems: "center" }}>
        <div style={{ fontWeight: 600 }}>
          Reel ready for scheduling
        </div>
        <Badge variant={verdictBadgeVariant(verdicts.reel_caption
          || result.gate_verdict)}>
          caption: {verdicts.reel_caption || result.gate_verdict}
        </Badge>
      </div>

      {/* Per-surface verdict pills + inline Sign off buttons */}
      <div
        style={{ display: "grid", gap: 8, marginTop: 12 }}
        data-testid="reel-upload-ok-surfaces"
      >
        {Object.entries(verdicts).map(([s, v]) => (
          <div
            key={s}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              padding: "6px 8px", borderRadius: 6,
              background: "#f7f7f8",
            }}
            data-testid={`reel-surface-row-${s}`}
          >
            <Badge
              variant={verdictBadgeVariant(v)}
              data-testid={`reel-upload-ok-${s}`}
            >
              {(surfaceLabel[s] || s)}: {signedOff[s] ? "signed off" : v}
            </Badge>
            {v === "needs_review" && !signedOff[s] && (
              <div style={{
                display: "flex", gap: 6, alignItems: "center",
                flex: 1, justifyContent: "flex-end",
              }}>
                <Input
                  placeholder="Note (optional)"
                  value={notes[s] || ""}
                  onChange={(e) => setNotes((p) =>
                    ({ ...p, [s]: e.target.value }))}
                  style={{ maxWidth: 240, height: 30 }}
                  data-testid={`reel-signoff-note-${s}`}
                />
                <Button
                  size="sm"
                  onClick={() => onSignoff(s)}
                  disabled={busySurface === s}
                  data-testid={`reel-signoff-btn-${s}`}
                >
                  {busySurface === s ? "Signing off…" : "Sign off"}
                </Button>
              </div>
            )}
            {v === "block" && (
              <span style={{ fontSize: 11, color: "#b00020",
                             marginLeft: 8 }}>
                blocked — re-upload with corrected text
              </span>
            )}
          </div>
        ))}
      </div>

      {signoffErr && (
        <div
          style={{ marginTop: 8, fontSize: 12, color: "crimson" }}
          data-testid="reel-signoff-error"
        >
          Sign off failed on{" "}
          <code>{surfaceLabel[signoffErr.surface] || signoffErr.surface}</code>:{" "}
          {signoffErr.detail?.message || "unknown error"}
        </div>
      )}

      {/* Freeze */}
      <div style={{ marginTop: 12, display: "flex", gap: 8,
                     alignItems: "center" }}>
        <TooltipProvider delayDuration={100}>
          <Tooltip>
            <TooltipTrigger asChild>
              {/* span wrapper lets the tooltip fire even when the
                   button is disabled */}
              <span>
                <Button
                  onClick={onFreeze}
                  disabled={!canFreeze || freezeBusy}
                  data-testid="reel-freeze-btn"
                >
                  {frozen
                    ? "Frozen ✓"
                    : freezeBusy
                      ? "Freezing…"
                      : "Freeze for publishing"}
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent data-testid="reel-freeze-tooltip">
              {blockerTooltip}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {frozen && (
          <span
            style={{ fontSize: 12, color: "#555" }}
            data-testid="reel-frozen-set-id"
          >
            asset_set_id: <code>{frozen.id}</code>
          </span>
        )}
      </div>

      {freezeErr && (
        <div
          style={{ marginTop: 8, fontSize: 12, color: "crimson" }}
          data-testid="reel-freeze-error"
        >
          <div style={{ fontWeight: 600 }}>
            {freezeErr.code || "Freeze refused"}
          </div>
          <div>{freezeErr.message}</div>
          {Array.isArray(freezeErr.blocked_surfaces)
            && freezeErr.blocked_surfaces.length > 0 && (
            <div>
              Blocked (re-upload):{" "}
              {freezeErr.blocked_surfaces
                .map((s) => surfaceLabel[s] || s).join(", ")}
            </div>
          )}
          {Array.isArray(freezeErr.unsigned_needs_review_surfaces)
            && freezeErr.unsigned_needs_review_surfaces.length > 0 && (
            <div>
              Awaiting sign-off:{" "}
              {freezeErr.unsigned_needs_review_surfaces
                .map((s) => surfaceLabel[s] || s).join(", ")}
            </div>
          )}
        </div>
      )}

      {/* Probe + public URL */}
      <div style={{ fontSize: 12, color: "#666", marginTop: 12 }}>
        {result.probe?.duration_s?.toFixed?.(1)}s ·{" "}
        {result.probe?.width}×{result.probe?.height} ·{" "}
        {(result.probe?.size_bytes / 1024 / 1024).toFixed(1)} MB
      </div>
      {result.video_url && (
        <>
          <video
            controls preload="metadata"
            style={{ maxWidth: 280, marginTop: 12 }}
            src={result.video_url}
            data-testid="reel-preview-player"
          />
          <div
            style={{ marginTop: 8, display: "flex", gap: 6,
                     alignItems: "center", flexWrap: "wrap" }}
            data-testid="reel-public-url-row"
          >
            <code
              style={{
                fontSize: 11, background: "#f4f4f5",
                padding: "3px 6px", borderRadius: 4,
                maxWidth: 420, overflow: "hidden",
                textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
              data-testid="reel-public-url"
              title={result.video_url}
            >
              {result.video_url}
            </code>
            <Button
              size="sm" variant="outline"
              onClick={onCopyUrl}
              data-testid="reel-public-url-copy"
            >
              {copied ? "Copied ✓" : "Copy URL"}
            </Button>
          </div>
        </>
      )}
      <div style={{ fontSize: 11, color: "#888", marginTop: 8 }}>
        reel_asset_id: {reelAssetId}
      </div>
    </Card>
  );
}
